<template>
  <div class="comment-item" :class="{ 'is-child': depth > 0 }">
    <div class="comment-avatar">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/></svg>
    </div>
    <div class="comment-body">
      <div class="comment-meta">
        <span class="comment-username">{{ comment.username }}</span>
        <span v-if="comment.is_admin" class="admin-badge">A</span>
        <span class="comment-time">{{ timeAgo(comment.created_at) }}</span>
      </div>
      <div class="comment-content">{{ comment.content }}</div>
      <div class="comment-actions">
        <template v-if="depth >= 5">
          <router-link :to="'/comment/' + comment.id" class="comment-action-btn comment-detail-link">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            {{ $t('view_detail') || '查看详情' }}
          </router-link>
        </template>
        <template v-else>
          <button class="comment-action-btn" @click="showReply = !showReply">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
            {{ $t('reply') }}
          </button>
        </template>
        <button class="comment-action-btn" @click="handleLike">
          <svg width="14" height="14" viewBox="0 0 24 24" :fill="comment.liked ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.5"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
          {{ comment.like_count || 0 }}
        </button>
        <button v-if="isAdmin" class="comment-action-btn" @click="handlePin" :style="comment.pinned ? 'color:var(--text-primary)' : ''">置顶</button>
        <button v-if="isAdmin" class="comment-action-btn" style="color:#d32f2f" @click="handleDelete">删除</button>
        <button v-if="isAdmin" class="comment-action-btn" @click="showDataDialog = true">数据</button>
        <button v-if="children.length > 0" class="comment-action-btn" @click="expanded = !expanded">
          {{ expanded ? $t('hide_replies') : `${$t('view_replies')} (${totalReplies})` }}
        </button>
      </div>

      <!-- Reply input -->
      <div v-if="showReply" class="reply-input-box mt-1">
        <input v-model="replyUsername" class="comment-username-input" :placeholder="$t('comment_username_placeholder')" maxlength="28" />
        <textarea
          v-model="replyContent"
          class="comment-textarea"
          :placeholder="$t('write_reply')"
          rows="2"
          maxlength="2000"
        />
        <div class="reply-input-actions">
          <button class="btn btn-sm" @click="showReply = false">{{ $t('cancel') }}</button>
          <button class="btn btn-sm btn-primary" @click="submitReply" :disabled="!canReply || replying">
            {{ replying ? '...' : $t('reply') }}
          </button>
        </div>
        <p v-if="replyError" class="comment-error">{{ replyError }}</p>
      </div>

      <!-- Children (nested replies) -->
      <div v-if="expanded && children.length > 0" class="comment-children">
        <CommentItem
          v-for="child in children"
          :key="child.id"
          :comment="child"
          :depth="depth + 1"
          :postId="postId"
          @reply="$emit('reply')"
          @refresh="$emit('refresh')"
        />
      </div>
    </div>

    <!-- Data dialog -->
    <div v-if="showDataDialog" class="modal-overlay" @click.self="showDataDialog = false">
      <div class="modal-box" style="max-width:420px">
        <div class="modal-title">评论数据</div>
        <div v-if="dataLoading" class="text-sm text-tertiary">Loading...</div>
        <template v-else-if="commentData">
          <div class="data-row"><span>用户:</span><span>{{ commentData.username }}</span></div>
          <div class="data-row"><span>IP:</span><span style="font-family:monospace;font-size:0.7rem">{{ commentData.ip_hash }}</span></div>
          <div class="data-row"><span>位置:</span><span>{{ commentData.country }} {{ commentData.region }} {{ commentData.city }}</span></div>
          <div class="data-row"><span>浏览器:</span><span class="text-xs">{{ commentData.browser }}</span></div>
          <div class="data-row"><span>时间:</span><span>{{ commentData.created_at }}</span></div>
          <div class="data-row"><span>状态:</span><span :style="commentData.is_banned ? 'color:#d32f2f' : ''">{{ commentData.is_banned ? '已封禁至 '+commentData.banned_until : '正常' }}</span></div>
          <div class="mt-2" style="display:flex;gap:6px;align-items:center">
            <input v-model="banDuration" class="input-field" style="width:100px;padding:6px 10px;font-size:0.8rem" placeholder="例: 30min, 24h, 7d" />
            <button class="btn btn-sm btn-danger" @click="handleBan" :disabled="banning">{{ banning ? '...' : '封禁IP' }}</button>
            <button v-if="commentData.is_banned" class="btn btn-sm" @click="handleUnban">解封</button>
          </div>
        </template>
        <div class="modal-actions mt-2"><button class="btn" @click="showDataDialog = false">关闭</button></div>
      </div>
    </div>
  </div>
</template>

<script>
import { replyComment, checkUsernameOnPost, toggleCommentLike, toggleCommentPin, getCommentInfo, banIP, unbanIP, deleteComment } from '../api'

export default {
  name: 'CommentItem',
  props: {
    comment: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    postId: { type: Number, required: true }
  },
  data() {
    return {
      showReply: false,
      replyUsername: localStorage.getItem('markjy_comment_username') || '',
      replyContent: '',
      replying: false,
      replyError: '',
      expanded: this.depth < 2,
      showDataDialog: false,
      commentData: null,
      dataLoading: false,
      banDuration: '',
      banning: false
    }
  },
  computed: {
    isAdmin() { return localStorage.getItem('markjy_is_admin') === 'true' },
    children() {
      return this.comment.children || []
    },
    totalReplies() {
      const count = (list) => {
        let n = 0
        for (const c of list) {
          n += 1 + count(c.children || [])
        }
        return n
      }
      return count(this.children)
    },
    canReply() {
      return this.replyUsername.trim().length > 0 && this.replyContent.trim().length > 0
    }
  },
  watch: {
    showDataDialog(val) {
      if (val) {
        this.dataLoading = true; this.commentData = null
        getCommentInfo(this.comment.id).then(res => { this.commentData = res.data.data }).finally(() => { this.dataLoading = false })
      }
    }
  },
  methods: {
    handleLike() {
      toggleCommentLike(this.comment.id).then(res => {
        this.comment.liked = res.data.data.liked
        this.comment.like_count = res.data.data.like_count
      }).catch(() => {})
    },
    handleDelete() {
      if (!confirm('确认删除此评论？')) return
      deleteComment(this.comment.id).then(() => this.$emit('refresh')).catch(() => {})
    },
    handleBan() {
      if (!this.banDuration) return
      this.banning = true
      banIP(this.commentData.ip_hash, this.banDuration).then(res => {
        this.commentData.is_banned = true
        this.commentData.banned_until = res.data.data.banned_until
      }).finally(() => { this.banning = false })
    },
    handleUnban() {
      unbanIP(this.commentData.ip_hash).then(() => {
        this.commentData.is_banned = false
        this.commentData.banned_until = ''
      })
    },
    handlePin() {
      toggleCommentPin(this.comment.id).then(res => {
        this.comment.pinned = res.data.data.pinned
        this.$emit('refresh')
      }).catch(() => {})
    },
    timeAgo(dateStr) {
      if (!dateStr) return ''
      const now = Date.now()
      const then = new Date(dateStr).getTime()
      const diff = Math.floor((now - then) / 1000)
      if (diff < 60) return 'just now'
      if (diff < 3600) return Math.floor(diff / 60) + 'm'
      if (diff < 86400) return Math.floor(diff / 3600) + 'h'
      if (diff < 2592000) return Math.floor(diff / 86400) + 'd'
      const d = new Date(dateStr)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    async submitReply() {
      if (!this.canReply) return
      this.replyError = ''
      this.replying = true

      try {
        const checkRes = await checkUsernameOnPost(this.postId, this.replyUsername.trim())
        if (!checkRes.data.data.available) {
          this.replyError = checkRes.data.data.reason
          this.replying = false
          return
        }

        await replyComment(this.comment.id, this.replyUsername.trim(), this.replyContent.trim())
        localStorage.setItem('markjy_comment_username', this.replyUsername.trim())
        this.replyContent = ''
        this.showReply = false
        this.expanded = true
        this.$emit('refresh')
      } catch (err) {
        const msg = err.response && err.response.data ? err.response.data.message : 'Network error'
        this.replyError = msg
      } finally {
        this.replying = false
      }
    }
  }
}
</script>

<style scoped>
.comment-item {
  display: flex;
  gap: 10px;
  padding: 14px 0;
  border-bottom: 1px solid var(--border-light);
}
.comment-item.is-child {
  border-bottom: none;
  padding: 10px 0 10px 0;
}
.comment-avatar {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
}
.is-child .comment-avatar {
  width: 26px;
  height: 26px;
}
.comment-body {
  flex: 1;
  min-width: 0;
}
.comment-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}
.comment-username {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
}
.admin-badge {
  font-size: 0.65rem;
  font-weight: 700;
  background: var(--text-primary);
  color: var(--bg-primary);
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.3;
}
.comment-time {
  font-size: 0.75rem;
  color: var(--text-tertiary);
}
.comment-content {
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--text-primary);
  white-space: pre-wrap;
  word-break: break-word;
}
.comment-actions {
  display: flex;
  gap: 16px;
  margin-top: 6px;
}
.comment-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--text-tertiary);
  font-size: 0.78rem;
  font-family: inherit;
  cursor: pointer;
  padding: 2px 0;
  transition: color 0.15s ease;
}
.comment-action-btn:hover {
  color: var(--text-secondary);
}
.comment-detail-link {
  text-decoration: none !important;
}
.comment-detail-link:hover {
  color: var(--text-secondary);
}
.reply-input-box {
  margin-top: 8px;
}
.comment-username-input {
  width: 100%;
  padding: 6px 0;
  border: none;
  border-bottom: 1px solid var(--border-light);
  background: transparent;
  color: var(--text-primary);
  font-size: 0.8rem;
  font-family: inherit;
  outline: none;
}
.comment-username-input::placeholder { color: var(--text-tertiary); }
.comment-textarea {
  width: 100%;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.85rem;
  font-family: inherit;
  resize: none;
  outline: none;
  padding: 4px 0;
  line-height: 1.5;
}
.comment-textarea::placeholder { color: var(--text-tertiary); }
.reply-input-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}
.comment-error {
  color: #d32f2f;
  font-size: 0.75rem;
  margin-top: 4px;
}
.data-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 0.8rem; border-bottom: 1px solid var(--border-light); }
.data-row span:first-child { color: var(--text-tertiary); }
.modal-overlay { position: fixed; inset: 0; background: var(--overlay); z-index: 2000; display: flex; align-items: center; justify-content: center; }
.modal-box { background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: 12px; padding: 24px; max-width: 380px; width: 90%; box-shadow: var(--shadow-lg); }
.modal-title { font-size: 1.05rem; font-weight: 600; margin-bottom: 8px; }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; }
.comment-children {
  margin-top: 4px;
  padding-left: 16px;
  border-left: 1px solid var(--border-light);
}
</style>
